// @flow
import * as React from 'react';
import { SchemaForm } from 'speed-js-react';
import { Box, Heading, Text } from 'grommet';
import { FormattedMessage } from 'react-intl';
import type { SchemaType } from 'speed-js-react/src/components/forms/types';
import useCreatablePersonField from 'esm-web-pro/hooks/useCreatablePersonField';
import memoizeOne from 'memoize-one';
import cloneDeep from 'lodash/cloneDeep';
import { set } from 'lodash';

type Props = {
    schema: ?SchemaType,
    initial: ?Object,
    onSubmit: (e: SyntheticEvent<HTMLFormElement>) => void,
    actions: () => void,
    renderSpecificCoverageSelect: () => void,
    nonFieldErrorsName: string,
}

const applyExtraSchema = memoizeOne((schemaResult, extraSchema) => {
    if (!extraSchema) {
        return schemaResult;
    }

    const schema = cloneDeep(schemaResult);
    if (extraSchema) {
        // Each entry is an object where the keys are the path to the property in the schema
        // and the values are the values for that property
        extraSchema.forEach((entry) => {
            // For each object element in the array gets the key and value pairs
            // and sets the value for that property in the schema object.
            Object.entries(entry).forEach((e) => {
                const [path, value] = e;
                const delimiterIndex = path.indexOf('.');
                const fieldName = path.substring(0, delimiterIndex);
                const pathName = path.substring(delimiterIndex + 1);
                const field = schema.fields.find((schemaField) => schemaField[0] === fieldName);
                if (field) {
                    set(field[1], pathName, value);
                }
            });
        });
    }
    return schema;
});

const CustomAddressRenderForm = (props: Props) => {
    const {
        schema, initial, onSubmit, actions, renderSpecificCoverageSelect, nonFieldErrorsName,
    } = props;

    const [extraSchema, formikRef, personCreator] = useCreatablePersonField('lfc');

    const renderForm = (properties, fields) => {
        const { handleSubmit, handleReset, errors } = properties;
        const nonFieldErrors: Array<string> = errors[nonFieldErrorsName];

        return (
            <Box height="100%" as="form" onSubmit={handleSubmit} onReset={handleReset}>
                <Box overflow="auto">
                    <Box
                        flex={{ shrink: 0 }}
                        pad={{ horizontal: 'medium', bottom: '120px' }}
                    >
                        <Box pad={{ top: 'small' }}>
                            <Box flex align="start">
                                <Heading level={5}>
                                    <FormattedMessage
                                        id="maptab-edit-address-mandatory-fields"
                                        defaultMessage="Mandatory Fields"
                                    />
                                </Heading>
                            </Box>
                            <Box>
                                {renderSpecificCoverageSelect}
                            </Box>
                        </Box>
                        <Box pad={{ top: 'small' }}>
                            <Box flex align="start">
                                <Heading level={5}>
                                    <FormattedMessage
                                        id="maptab-edit-address-optional-fields"
                                        defaultMessage="Optional Fields"
                                    />
                                </Heading>
                            </Box>
                            <Box>
                                <Box gap="small">
                                    {nonFieldErrors && (
                                        <Box
                                            background="status-error"
                                            margin={{ vertical: 'small' }}
                                            pad="small"
                                            round="small"
                                        >
                                            <Text color="white">{nonFieldErrors.join('; ')}</Text>
                                        </Box>
                                    )}
                                    {Object.values(fields)}
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>
                {actions}
            </Box>
        );
    };

    return (
        <>
            <SchemaForm
                schema={applyExtraSchema(schema, extraSchema)}
                initial={initial}
                onSubmit={onSubmit}
                formikRef={formikRef}
            >
                {renderForm}
            </SchemaForm>
            {personCreator}
        </>
    );
};

export default CustomAddressRenderForm;
